/**
 * ClientLabelledBadge -- "this shirt carries a client's neck label".
 *
 * A floor that holds the shirt in more than one label (its own, and a client's)
 * must say which one to take. The badge names the client ("Threadheads
 * labelled") and falls back to the generic text when the name is unknown.
 *
 * Teal is a chip family no other picklist chip uses (amber redo, sky requeued,
 * fuchsia DTF, violet custom). Colour is never the only channel
 * (PRODUCTION_DESIGN §13): the Tag icon and the text carry it, and the printed
 * pick list repeats the text inside a border (`clientLabelledLabel`, in its
 * own file).
 *
 * The label is capped and truncates so a long client name never pushes a row
 * past its h-16.
 */
import { useTranslation } from 'react-i18next';
import { Tag } from 'lucide-react';
import { cn } from '@/lib/utils';
import { clientLabelledLabel } from './clientLabelledLabel';

export interface ClientLabelledBadgeProps {
  clientName?: string | null;
  className?: string;
}

export function ClientLabelledBadge({ clientName, className }: ClientLabelledBadgeProps) {
  const { t } = useTranslation('production');
  const label = clientLabelledLabel(t, clientName);
  return (
    <span
      className={cn(
        'inline-flex max-w-[16rem] items-center gap-1 rounded-full border border-teal-600 bg-teal-100 px-2 py-0.5 text-sm font-semibold text-teal-800 dark:border-teal-500 dark:bg-teal-900/30 dark:text-teal-200',
        className,
      )}
    >
      <Tag className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span className="truncate">{label}</span>
    </span>
  );
}
